/**
 * NMS Sale Featured Product Component
 * Every colour of both genders is already rendered as a hidden panel, so the gender toggle
 * and colour swatches only swap which panel is visible - no request. They are links to the
 * product pages, which is what happens without this file.
 *
 * "Grab the deal" is the PDP add to cart: nms-pdp-size-integration.js opens the size popup
 * and submits the theme's <product-form>. That script treats the first size container on the
 * page as the "main" form and writes the picked size there too, so the size is cleared from
 * every panel after each add - otherwise the next colour would skip the popup.
 */

// Text that eases in on a switch when it differs from the panel before
const TEXT_FADE_SELECTOR = [
  '.nms-sale-featured-product__deal-value',
  '.nms-sale-featured-product__stock',
  '.nms-sale-featured-product__title',
  '.nms-sale-featured-product__rating',
  '.nms-sale-featured-product__color-label',
  '.nms-sale-featured-product__prices',
  '.nms-sale-featured-product__saving',
].join(', ');

class NMSSaleFeaturedProduct extends window.NMSBaseClass {
  #controller = null;
  // Last panel shown per gender, so flipping Men -> Women -> Men keeps the chosen colour
  #selectedPanels = {};
  #countdownTimer = null;

  constructor(element) {
    super();

    // REQUIRED: Store element reference for scoped operations
    this.element = element;
    this.sectionId = element.dataset.sectionId || `section-${Date.now()}`;

    // REQUIRED: Register instance in global namespace for external access
    window.neemans = window.neemans || {};
    window.neemans.saleFeaturedProduct = window.neemans.saleFeaturedProduct || {};
    window.neemans.saleFeaturedProduct[this.sectionId] = this;
  }

  init() {
    // REQUIRED: Override base init to scope operations to this.element
    this.#setupComponentRefs();
    if (this.refs.panels.length === 0) return;

    // The first panel of each gender is the product picked in the editor
    for (const panel of this.refs.panels) {
      this.#selectedPanels[panel.dataset.gender] ??= panel.id;
    }

    const activePanel = this.refs.panels.find(panel => !panel.hidden);
    if (activePanel) this.#selectedPanels[activePanel.dataset.gender] = activePanel.id;

    this.#bindComponentEvents();
    this.#startStockCountdown();
  }

  destroy() {
    this.#controller?.abort();
    clearTimeout(this.#countdownTimer);
    super.destroy();

    if (window.neemans.saleFeaturedProduct?.[this.sectionId]) {
      delete window.neemans.saleFeaturedProduct[this.sectionId];
    }
  }

  // REQUIRED: Component-scoped ref setup (override base class)
  #setupComponentRefs() {
    this.refs = {
      panels: Array.from(this.element.querySelectorAll('[data-panel]')),
    };
  }

  #bindComponentEvents() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.element.addEventListener('click', event => this.#onClick(event), { signal });

    // Start fetching a colour's shoe as soon as the pointer or focus reaches its control
    for (const type of ['pointerover', 'touchstart', 'focusin']) {
      this.element.addEventListener(type, event => this.#onIntent(event), { signal, passive: true });
    }

    // The theme's <product-form> fires these on the form once /cart/add answers
    this.element.addEventListener('variant:add', () => this.#clearPickedSizes(), { signal });
    this.element.addEventListener('cart:error', () => this.#clearPickedSizes(), { signal });
  }

  /**
   * The section's stock countdown (start/end as Unix seconds): [count] falls from each gender's
   * quantity to 0 between the two, once a minute, rounded up - the same sum the section's Liquid
   * does. The page may be served from cache, so the rendered number is refreshed straight away.
   */
  #startStockCountdown() {
    const { stockStart, stockEnd, stockQuantityMen, stockQuantityWomen } = this.element.dataset;
    const start = Number(stockStart);
    const end = Number(stockEnd);
    const quantities = { men: Number(stockQuantityMen), women: Number(stockQuantityWomen) };
    if (!start || !(end > start)) return;

    const update = () => {
      const now = Math.floor(Date.now() / 60000) * 60;
      const left = Math.min(Math.max(end - now, 0), end - start);
      let remaining = 0;

      for (const counter of this.element.querySelectorAll('[data-stock-count]')) {
        const quantity = quantities[counter.closest('[data-panel]').dataset.gender] || 0;
        const count = Math.ceil((quantity * left) / (end - start));
        counter.textContent = count;
        counter.closest('.nms-sale-featured-product__stock').hidden = count === 0;
        remaining += count;
      }

      // Next tick on the minute boundary; stop once every count reaches 0
      if (remaining > 0) {
        this.#countdownTimer = setTimeout(update, 60000 - (Date.now() % 60000) + 50);
      }
    };

    update();
  }

  #onIntent(event) {
    const control = event.target.closest?.('[data-panel-target], [data-gender-target]');
    if (!control) return;

    const panelId = control.dataset.panelTarget || this.#selectedPanels[control.dataset.genderTarget];
    this.#loadImage(this.refs.panels.find(panel => panel.id === panelId));
  }

  // Hidden panels keep their lazy image unloaded; eager makes the browser fetch it now
  #loadImage(panel) {
    const image = panel?.querySelector('.nms-sale-featured-product__image');
    if (image && image.loading !== 'eager') image.loading = 'eager';
    return image;
  }

  /**
   * Flag the text that differs from the panel being left, so only that fades in. Colours of
   * one product share name and price, so a swatch switch only moves the shoe (and the stock).
   * Matching is by class and position, since a panel may lack a block such as the rating.
   */
  #markChangedText(previousPanel, nextPanel) {
    const textOf = panel => {
      const texts = new Map();
      for (const element of panel?.querySelectorAll(TEXT_FADE_SELECTOR) ?? []) {
        const key = element.classList[0];
        texts.set(key, [...(texts.get(key) ?? []), element.textContent.trim()]);
      }
      return texts;
    };

    const previousTexts = textOf(previousPanel);
    const seen = new Map();

    for (const element of nextPanel.querySelectorAll(TEXT_FADE_SELECTOR)) {
      const key = element.classList[0];
      const index = seen.get(key) ?? 0;
      seen.set(key, index + 1);
      element.classList.toggle('is-changed', previousTexts.get(key)?.[index] !== element.textContent.trim());
    }
  }

  #clearPickedSizes() {
    for (const input of this.element.querySelectorAll('[data-sale-featured-variant-id]')) {
      // Products without sizes keep their single variant id
      if (input.form?.querySelector('[data-nms-pdp-size-integration]')) {
        input.value = '';
      }
    }
  }

  #onClick(event) {
    // Let ctrl/cmd/shift-click open the product page in a new tab
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const control = event.target.closest('[data-panel-target], [data-gender-target]');
    if (!control || !this.element.contains(control)) return;

    if (control.dataset.genderTarget) {
      const gender = control.dataset.genderTarget;
      const panelId = this.#selectedPanels[gender];
      if (!panelId) return;

      event.preventDefault();
      this.showPanel(panelId, `[data-gender-target="${gender}"]`);
      return;
    }

    event.preventDefault();
    this.showPanel(control.dataset.panelTarget, `[data-panel-target="${CSS.escape(control.dataset.panelTarget)}"]`);
  }

  /**
   * Show one panel and hide the rest
   * @param {string} panelId - id of the panel to show
   * @param {string} [focusSelector] - control to focus in the new panel, so keyboard focus
   *   isn't lost when the clicked one gets hidden
   */
  showPanel(panelId, focusSelector) {
    const nextPanel = this.refs.panels.find(panel => panel.id === panelId);
    if (!nextPanel || !nextPanel.hidden) return;

    this.#markChangedText(this.refs.panels.find(panel => !panel.hidden), nextPanel);

    for (const panel of this.refs.panels) {
      panel.hidden = panel !== nextPanel;
      panel.classList.remove('is-entering', 'is-image-pending');
    }

    // Restart the ease-in even when switching back to a panel shown before
    void nextPanel.offsetWidth;
    nextPanel.classList.add('is-entering');

    // A shoe that is still downloading stays hidden and slides in once it arrives, not mid-way
    const image = this.#loadImage(nextPanel);
    if (image && !(image.complete && image.naturalWidth)) {
      nextPanel.classList.add('is-image-pending');
      const reveal = () => nextPanel.classList.remove('is-image-pending');
      image.addEventListener('load', reveal, { once: true });
      image.addEventListener('error', reveal, { once: true });
    }

    this.#selectedPanels[nextPanel.dataset.gender] = panelId;

    if (focusSelector) {
      nextPanel.querySelector(focusSelector)?.focus({ preventScroll: true });
    }

    this.dispatchNMSEvent('sale-featured-product-changed', {
      sectionId: this.sectionId,
      panelId,
      gender: nextPanel.dataset.gender,
    });
  }
}

// REQUIRED: Standard component initialization pattern
function initNMSSaleFeaturedProduct() {
  if (!window.NMSBaseClass) {
    console.error('NMS Sale Featured Product: NMSBaseClass not found. Make sure nms-base-class.js loads first.');
    return;
  }

  const elements = document.querySelectorAll('nms-sale-featured-product');

  for (const element of elements) {
    if (element.hasAttribute('data-initialized')) continue;

    try {
      const instance = new NMSSaleFeaturedProduct(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    } catch (error) {
      console.error('Failed to initialize NMS Sale Featured Product:', error, element);
    }
  }
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSSaleFeaturedProduct);
} else {
  initNMSSaleFeaturedProduct();
}

// Re-initialize when new content is added (theme editor, AJAX, etc.)
document.addEventListener('nms:content-updated', initNMSSaleFeaturedProduct);
document.addEventListener('shopify:section:load', initNMSSaleFeaturedProduct);

// Export for debugging and manual initialization
window.initNMSSaleFeaturedProduct = initNMSSaleFeaturedProduct;

// Global API for accessing instances
window.getNMSSaleFeaturedProduct = function (sectionId) {
  if (sectionId) {
    return window.neemans?.saleFeaturedProduct?.[sectionId] || null;
  }
  return window.neemans?.saleFeaturedProduct || {};
};
